// src/features/manual/types.ts
//
// The shapes of `GET /api/v1/ui/manual/*` (roommatik.analytics >= 2.26.0), as the API sends them
// inside its `{ success, data }` envelope.

export type ManualLang = 'es' | 'en';
export const MANUAL_LANGS: readonly ManualLang[] = ['es', 'en'];

/** `staff`: only Roommatik reads the chapter (the API never lists it for a customer). */
export type ManualAudience = 'all' | 'staff';

export interface ManualChapterSummary {
  id: string;
  order: number;
  title: string;
  audience: ManualAudience;
}

export interface ManualPart {
  part: string;
  chapters: ManualChapterSummary[];
}

/** An Insight screen and the manual section that explains it (claimed by the section's `Screens:` line). */
export interface ManualScreenLink {
  screen: string;
  chapterId: string;
  anchor: string;
}

export interface ManualIndex {
  lang: ManualLang;
  parts: ManualPart[];
  screens: ManualScreenLink[];
}

export interface ManualSection {
  anchor: string;
  heading: string;
  level: number;
  screens: string[];
}

export interface ManualChapter {
  id: string;
  part: string;
  order: number;
  title: string;
  lang: ManualLang;
  audience: ManualAudience;
  updated?: string | null;
  sections: ManualSection[];
  /** Markdown. Staff receive the `:::staff` fences; customers never receive those blocks. */
  body: string;
}

/** Where a search hit was found; the API ranks title, then heading, then body. */
export type ManualSearchMatch = 'title' | 'heading' | 'body';

/** One hit of `GET /api/v1/ui/manual/search` (roommatik.analytics >= 2.27.0). */
export interface ManualSearchResult {
  chapterId: string;
  chapterTitle: string;
  part: string;
  /** Section to open; null for the chapter title or text before the first heading. */
  anchor: string | null;
  heading: string | null;
  /** Plain text around the first hit (no Markdown). */
  snippet: string;
  match: ManualSearchMatch;
  /** The hit is in text only Roommatik staff read (never sent to a customer). */
  staff: boolean;
}

export interface ManualSearchResponse {
  lang: ManualLang;
  /** The terms the API matched: lower case, without accents. */
  terms: string[];
  /** Matches before the result limit. */
  total: number;
  results: ManualSearchResult[];
}
